import React, {useState, useEffect} from "react";
import "@chatscope/chat-ui-kit-styles/dist/default/styles.css";
import "prismjs/themes/prism-okaidia.css";
import {Rnd} from "react-rnd";
import {
  MainContainer,
  ChatContainer,
  ConversationHeader,
  Avatar,
  MessageList,
  Message,
  MessageInput,
  TypingIndicator,
} from "@chatscope/chat-ui-kit-react";
import Prism from "prismjs";
import {marked} from "marked";
import {useSelector} from "react-redux";

import "./index.css";
import {sendMessage, createThread} from "../../api-chatbot/chat.js";
import iconClose from "images/ic_chatGPT_close.svg";
import iconPlus from "images/ic_add_chat.svg";
import gptAvatar from "images/ic_gpt_avatar.png";
import {TwoBtnPopUp, showPopUp} from "components/PopUp";

const Chat = ({handleClose, messages, setMessages}) => {
  const {userinfo} = useSelector(state => state);

  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    Prism.highlightAll();
  }, [messages]);

  const confirmCreateThread = async () => {
    try {
      await createThread({
        userId: userinfo.id,
      });
      // Reset messages and indicate bot is not typing after creating a new thread
      setMessages([
        {
          message: "Hello, I'm Redbrick Support bot! Ask me anything!",
          messageHtml: "<p>Hello, I'm Redbrick Support bot! Ask me anything!</p>",
          sentTime: "just now",
          sender: "ChatGPT",
        },
      ]);
      setIsTyping(false);
    } catch (e) {
      console.error("Failed to create new thread!", e);
    }
  };

  const handleCreateThread = () => {
    showPopUp(
      <TwoBtnPopUp
        title="Are you sure you want to create a new GPT chat? Previous messages will be deleted."
        confirmBtn="Confirm"
        cancelBtn="Cancel"
        confirmAction={confirmCreateThread}
      />,
      {
        defaultPadding: false,
        noBackground: true,
      },
    );
  };

  const handleSendRequest = async message => {
    // Remove HTML tags to ensure message is plain text
    message = message.replace(/<br\s*\/?>/gi, "\n").replace(/<\/?[^>]+(>|$)/g, "");

    const newMessage = {
      message,
      direction: "outgoing",
      sender: "user",
    };

    setMessages(prevMessages => [...prevMessages, newMessage]);
    setIsTyping(true);

    try {
      const content = await getOpenAIResponse([...messages, newMessage]);
      if (content) {
        const chatGPTResponse = {
          message: content.content,
          messageHtml: content.contentHtml,
          sender: "ChatGPT",
        };
        setMessages(prevMessages => [...prevMessages, chatGPTResponse]);
      }
    } catch (error) {
      console.error("Error processing message:", error);
    } finally {
      setIsTyping(false);
    }
  };

  async function getOpenAIResponse(chatMessages) {
    const message = chatMessages[chatMessages.length - 1];

    const lastMessageForRun = await sendMessage({
      userId: userinfo.id,
      message: message.message,
    });
    const responseInHtml = marked(lastMessageForRun.data.reply);

    return {
      content: lastMessageForRun.reply,
      contentHtml: responseInHtml,
    };
  }

  return (
    <Rnd
      default={{
        x: 0,
        y: 100,
        width: 460,
        height: 483,
      }}
      minWidth="460"
      minHeight="483"
      cancel=".cs-message-list"
      className="chat"
    >
      <MainContainer style={{fontSize: "1em"}}>
        <ChatContainer>
          <ConversationHeader>
            <ConversationHeader.Content userName="Redbrick Support Bot">
              <span
                style={{
                  color: "#000",
                  alignSelf: "center",
                }}
              >
                AI Assistant
              </span>
            </ConversationHeader.Content>
          </ConversationHeader>

          <MessageList
            className="cs-message-list"
            scrollBehavior="auto"
            typingIndicator={isTyping ? <TypingIndicator content="Redbrick AI Assistant is typing" /> : null}
          >
            {messages.map((message, i) => {
              return message.sender === "user" ? (
                <Message key={i} model={message} />
              ) : (
                <Message
                  key={i}
                  model={{
                    type: "html",
                    direction: "incoming",
                  }}
                >
                  <Avatar size="md" src={gptAvatar} name={"AI Assistant"} />
                  <Message.HtmlContent html={message.messageHtml} />
                </Message>
              );
            })}
          </MessageList>
          <MessageInput
            autoFocus
            fancyScroll={false}
            attachButton={false}
            placeholder="Ask Redbrick AI Assistant here..."
            onSend={handleSendRequest}
          ></MessageInput>
        </ChatContainer>
      </MainContainer>
      <img className="code-editor__close-button" src={iconClose} alt="close" onClick={handleClose} />
      <img className="code-editor__new-button" src={iconPlus} alt="newThread" onClick={handleCreateThread} />
    </Rnd>
  );
};

export default Chat;
